import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { checkIfLiveEvent, sportIdToSportType } from 'toolkit/utils';
import {
  fetchFullEventReq,
  fetchTopMatchesReq,
  fetchClearFavoritesReq,
  fetchLiveMatchesReq,
  fetchSportCountsWithHoursReq,
  fetchSportCountsReq,
  fetchCompetitorsNameByLanguageReq,
  fetchEventsByCategoryReq,
  fetchEventsByCategoryWithHoursReq,
  fetchEventsBySportReq,
  fetchNextLiveMatchesReq,
  toggleFavoriteReq,
  fetchUserFavoritesReq,
  fetchBetBuilderAvailableSelectionsReq,
  fetchMaradonaHandicapReq
} from './services';
import { getBetSlipMatches } from '../betSlip';
import { eventTimeToSeconds } from 'toolkit/utils';
import { EnumMatchStatusName } from 'toolkit/Enums';
import { sports } from 'toolkit/constants';
import { filterAllowedEvents } from 'toolkit/allowedSports';

const initialState = {
  events: {},
  counts: {},
  timeCounts: {},
  clearFavorites: [],
  userFavorites: [], // User's starred favorites
  liveMatches: [],
  nextLiveMatches: [],
  nextLiveMatchesMeta: {},
  topMatches: [],
  filteredEventIds: [],
  selectedCategory: null,
  selectedTournament: null,
  producerStatus: {
    live: false,
    preMatch: false
  },
  selectedMatch: null,
  selectedSport: null,
  selectedClearFavoritesSport: null,
  selectedUserFavoritesSport: null,
  selectedLiveSportDesktop: null,
  selectedLeague: '',
  desktopSearchParams: {
    mode: 'topMatches',
    Sport: null,
    Category: null,
    Tournament: null,
    dateFilter: '0'
  },
  categoryNames: {},
  tournamentNames: {},
  selectedLocale: 'en',
  locales: {
    tr: {},
    de: {},
    en: {},
    nl: {},
    bg: {},
    fr: {}
  },
  betBuilderAvailableSelections: {},
  betBuilderLoading: false,
  betBuilderError: null,
  maradonaHandicap: []
};

export const fetchFullEvent = createAsyncThunk('match/fetchFullEvent', async (providerId) => {
  const response = await fetchFullEventReq({ gameNumber: providerId });
  return response;
});

export const fetchTopMatches = createAsyncThunk('match/fetchTopMatches', async (params) => {
  const response = await fetchTopMatchesReq(params);
  return response;
});

export const fetchClearFavorites = createAsyncThunk('match/fetchClearFavorites', async (params) => {
  const response = await fetchClearFavoritesReq(params);
  return response;
});

export const fetchLiveMatches = createAsyncThunk('match/fetchLiveMatches', async (params) => {
  const response = await fetchLiveMatchesReq(params);
  return response;
});

export const fetchNextLiveMatches = createAsyncThunk(
  'match/fetchNextLiveMatches',
  async (params) => {
    const response = await fetchNextLiveMatchesReq(params);
    return response;
  }
);

export const fetchSportCountsWithHours = createAsyncThunk(
  'match/fetchSportCountsWithHours',
  async (params) => {
    const response = await fetchSportCountsWithHoursReq(params);
    return response;
  }
);

export const fetchSportCounts = createAsyncThunk('match/fetchSportCounts', async (params) => {
  const response = await fetchSportCountsReq(params);
  return response;
});

export const fetchEventsByCategoryWithHours = createAsyncThunk(
  'match/fetchEventsByCategoryWithHours',
  async (data) => {
    const response = await fetchEventsByCategoryWithHoursReq(data);
    return response;
  }
);

export const fetchEventsByCategory = createAsyncThunk(
  'match/fetchEventsByCategory',
  async (data) => {
    const response = await fetchEventsByCategoryReq(data);
    return response;
  }
);

export const fetchEventsBySport = createAsyncThunk('match/fetchEventsBySport', async (data) => {
  const response = await fetchEventsBySportReq(data);
  return response;
});

export const fetchCompetitorsNameByLanguage = createAsyncThunk(
  'match/fetchCompetitorsNameByLanguage',
  async (language) => {
    const lang = language.id.split('-')[0];
    const response = await fetchCompetitorsNameByLanguageReq(lang);
    return {
      language,
      data: response
    };
  }
);

export const fetchBetBuilderAvailableSelections = createAsyncThunk(
  'match/fetchBetBuilderAvailableSelections',
  async (matchId) => {
    const response = await fetchBetBuilderAvailableSelectionsReq(matchId);
    return {
      matchId,
      data: response
    };
  }
);

export const fetchMaradonaHandicap = createAsyncThunk(
  'match/fetchMaradonaHandicap',
  async (params) => {
    const response = await fetchMaradonaHandicapReq(params);
    return response;
  }
);

export const toggleFavorite = createAsyncThunk(
  'match/toggleFavorite',
  async ({ gameNumber, sportType }, { rejectWithValue }) => {
    try {
      const response = await toggleFavoriteReq({
        gameNumber,
        sportType: String(sportType)
      });
      const message =
        response?.messages?.[0]?.text || response?.data?.messages?.[0]?.text || null;
      return { 
        gameNumber, 
        success: response.success || response.isSuccess, 
        message,
        isFavorite: response?.data?.isFavorite
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchUserFavorites = createAsyncThunk('match/fetchUserFavorites', async () => {
  const response = await fetchUserFavoritesReq();
  return response;
});

// A market is uniquely identified by its stringId. Occasionally the same market
// arrives more than once in a single payload (e.g. the provider sends one
// INACTIVE and one ACTIVE copy). Keep a single entry per stringId, preferring
// the ACTIVE (marketStatus === 6) one, so it is never rendered as a duplicate.
// Markets with different stringIds (distinct lines / specifiers) are all kept,
// including suspended ones.
const ACTIVE_MARKET_STATUS = 6;
const dedupeMarketsByStringId = (markets) => {
  if (!Array.isArray(markets)) return markets;
  const indexByStringId = new Map();
  const result = [];
  markets.forEach((market) => {
    const stringId = market?.stringId;
    if (!stringId) {
      result.push(market);
      return;
    }
    const existingIndex = indexByStringId.get(stringId);
    if (existingIndex === undefined) {
      indexByStringId.set(stringId, result.length);
      result.push(market);
    } else if (
      market.marketStatus === ACTIVE_MARKET_STATUS &&
      result[existingIndex].marketStatus !== ACTIVE_MARKET_STATUS
    ) {
      // Duplicate stringId: prefer the ACTIVE copy over a non-active one.
      result[existingIndex] = market;
    }
  });
  return result;
};

// Markets that must only ever appear ONCE per event (single-instance markets:
// RoM = 7, next goal = 8). For these ids we also collapse same-id duplicates
// that differ only by a stale specifier (different stringId), keeping the ACTIVE
// copy. Edit this list to add more single-instance market ids.
const DEDUPE_BY_ID_MARKET_IDS = [7, 8];
const dedupeMarketsById = (markets) => {
  if (!Array.isArray(markets)) return markets;
  const winnerById = new Map();
  markets.forEach((market) => {
    const id = market?.id;
    if (!DEDUPE_BY_ID_MARKET_IDS.includes(id)) return;
    const current = winnerById.get(id);
    if (!current) {
      winnerById.set(id, market);
      return;
    }
    const currentActive = current.marketStatus === ACTIVE_MARKET_STATUS;
    const marketActive = market.marketStatus === ACTIVE_MARKET_STATUS;
    // Prefer the ACTIVE copy; if both (or neither) are active, keep the later one.
    if (marketActive || !currentActive) {
      winnerById.set(id, market);
    }
  });
  if (winnerById.size === 0) return markets;
  const emitted = new Set();
  const result = [];
  markets.forEach((market) => {
    const id = market?.id;
    if (DEDUPE_BY_ID_MARKET_IDS.includes(id)) {
      if (emitted.has(id)) return;
      emitted.add(id);
      result.push(winnerById.get(id));
    } else {
      result.push(market);
    }
  });
  return result;
};

// Full de-duplication for an event's market list:
//   1. collapse same-stringId copies (ALL markets), then
//   2. collapse same-id copies for the hardcoded single-instance ids (7, 8),
// always preferring the ACTIVE (marketStatus === 6) copy.
const dedupeEventMarkets = (markets) => dedupeMarketsById(dedupeMarketsByStringId(markets));

const addMatchesToState = (state, events, order, counts) => {
  events = filterAllowedEvents(events);
  events.forEach((event) => {
    // Filter new markets to only include those with outcomes, and drop
    // duplicate markets (same stringId always; same id for 7/8).
    const newMarkets = dedupeEventMarkets(
      event.markets ? event.markets.filter((m) => m.outcomes && m.outcomes.length > 0) : []
    );

    // Get existing event from state
    const existingEvent = state[event.providerId];

    // Merge markets: preserve existing ones and add/update new ones
    let mergedMarkets = newMarkets;
    if (existingEvent && existingEvent.markets && existingEvent.markets.length > 0) {
      // Create a map of existing markets by stringId for quick lookup
      const marketsMap = new Map();
      const marketsWithoutStringId = [];

      // First, add all existing markets (preserve them - they might be in betslip)
      existingEvent.markets.forEach((market) => {
        if (market.stringId) {
          marketsMap.set(market.stringId, market);
        } else {
          // Keep markets without stringId separately
          marketsWithoutStringId.push(market);
        }
      });

      // Then, update or add new markets (this will update existing ones or add new ones)
      newMarkets.forEach((market) => {
        if (market.stringId) {
          marketsMap.set(market.stringId, market);
        } else {
          // Add new markets without stringId
          marketsWithoutStringId.push(market);
        }
      });

      // Combine: keep all existing markets (they might be in betslip) and update/add new ones
      mergedMarkets = [...Array.from(marketsMap.values()), ...marketsWithoutStringId];
    }

    // Preserve existing favorite flag if present in state
    const mergedEvent = {
      ...event,
      markets: mergedMarkets,
      isFavorite:
        existingEvent?.isFavorite !== undefined
          ? existingEvent.isFavorite
          : event.isFavorite ?? false
    };

    setMatch(state, mergedEvent, order);
    if (counts) {
      setCount(counts, event);
    }
  });
};

const setMatch = (events, event, order) => {
  if (!event) return;
  if ((events[event.providerId]?.lastUpdateOrder ?? -1) > order) {
    return;
  }
  // Final safety net for every write path (incl. updateMatch/BetEventUpdated):
  // never store duplicate markets (same stringId always; same id for 7/8);
  // keep the ACTIVE copy.
  // eslint-disable-next-line no-param-reassign
  event = Array.isArray(event.markets)
    ? { ...event, markets: dedupeEventMarkets(event.markets) }
    : event;
  const existingEvent = events[event.providerId];
  
  // Preserve existing favorite flag if present in state
  const isFavorite =
    existingEvent?.isFavorite !== undefined
      ? existingEvent.isFavorite
      : event.isFavorite ?? false;

  if (checkIfLiveEvent(event)) {
    events[event.providerId] = {
      ...event,
      isFavorite,
      timeTextInternal: event.timeText
        ? eventTimeToSeconds(event.timeText)
        : existingEvent?.timeTextInternal ?? 0,
      timeTextStoppageInternal: event.timeTextStoppage
        ? eventTimeToSeconds(event.timeTextStoppage)
        : existingEvent?.timeTextStoppageInternal ?? 0,
      lastUpdateOrder: order,
      LastUpdateTime: Date.now()
    };
  } else {
    events[event.providerId] = { ...event, isFavorite, lastUpdateOrder: order };
  }
};

const setCount = (counts, event, isDelete = false) => {
  if (!event) return;
  const { sport, categoryId, leagueId } = event;
  const sportName = sportIdToSportType(String(sport));
  if (!counts[sportName]) {
    counts[sportName] = {};
  }
  if (!counts[sportName][categoryId]) {
    counts[sportName][categoryId] = {};
  }
  if (counts[sportName][categoryId][leagueId] === undefined) {
    counts[sportName][categoryId][leagueId] = 0;
  }
  if (!isDelete) {
    counts[sportName][categoryId][leagueId] += 1;
  } else {
    counts[sportName][categoryId][leagueId] -= 1;
  }
};

const setTournamentName = (tournamentNames, event) => {
  const { leagueId, league } = event;
  if (leagueId && league) {
    tournamentNames[leagueId] = league;
  }
};

export const matchSlice = createSlice({
  name: 'match',
  initialState,
  reducers: {
    setSelectedMatch: (state, action) => {
      state.selectedMatch = action.payload;
    },
    setProducerStatus: (state, action) => {
      const { producers } = action.payload;
      state.producerStatus = producers;
    },
    setSelectedLocale: (state, action) => {
      state.selectedLocale = action.payload;
    },
    setLocales: (state, action) => {
      const { locales } = action.payload;
      const currentLocales = { ...state.locales };
      Object.keys(locales).forEach((lang) => {
        currentLocales[lang] = {
          ...currentLocales[lang],
          ...locales[lang]
        };
      });
      return {
        ...state,
        locales: currentLocales
      };
    },
    setSelectedSport: (state, action) => {
      state.selectedSport = action.payload;
    },
    setSelectedClearFavoritesSport: (state, action) => {
      state.selectedClearFavoritesSport = action.payload;
    },
    setCategoryNames: (state, action) => {
      state.categoryNames = action.payload;
    },
    addMatch: (state, action) => {
      const { events: rawEvents = [], order } = action.payload;
      const events = filterAllowedEvents(rawEvents);

      // We control the order of the each event inside the addMatchToState function before actually adding them to the state
      addMatchesToState(state.events, events, order, state.counts);

      events.forEach((event) => {
        // Old update msg we should skip it, we might have get the update msg before the add msg
        if ((events[event.providerId]?.lastUpdateOrder ?? -1) > order) {
          return;
        }

        if (checkIfLiveEvent(event)) {
          if (state.liveMatches.indexOf(event.providerId) === -1) {
            state.liveMatches.push(event.providerId);
          }
          state.nextLiveMatches = state.nextLiveMatches.filter((m) => m !== event.providerId);
        }

        setTournamentName(state.tournamentNames, event);
      });
    },
    updateMatch: (state, action) => {
      const { events: rawEvents = [], order } = action.payload;
      const events = filterAllowedEvents(rawEvents);
      events.forEach((event) => {
        if ((state.events[event.providerId]?.lastUpdateOrder ?? -1) > order) {
          return;
        }

        // Preserve existing favorite flag if present in state
        const existingEvent = state.events[event.providerId];
        const mergedEvent = {
          ...event,
          isFavorite:
            existingEvent?.isFavorite !== undefined
              ? existingEvent.isFavorite
              : event.isFavorite ?? false
        };

        if (checkIfLiveEvent(event)) {
          if (state.liveMatches.indexOf(event.providerId) === -1) {
            state.liveMatches.push(event.providerId);
          }

          state.nextLiveMatches = state.nextLiveMatches.filter((m) => m !== event.providerId);
        }

        setMatch(state.events, mergedEvent, order);
        setTournamentName(state.tournamentNames, mergedEvent);
      });
    },
    deleteMatch: (state, action) => {
      const { events = [] } = action.payload;
      events.forEach((event) => {
        if (state.liveMatches.indexOf(event)) {
          state.liveMatches = state.liveMatches.filter((l) => l !== event);
        }
        setCount(state.counts, state.events[event], true);
        delete state.events[event];
      });
    },
    setSelectedLiveDesktopSport: (state, action) => {
      state.selectedLiveSportDesktop = action.payload;
    },
    setDesktopSearchParams: (state, action) => {
      state.desktopSearchParams = {
        ...state.desktopSearchParams,
        ...action.payload
      };
    },
    setNextLiveMatches: (state) => {
      state.nextLiveMatches = [];
      state.nextLiveMatchesMeta = {};
    },
    setSelectedLeague: (state, action) => {
      state.selectedLeague = action.payload;
    },
    updateLiveFootballEventsTime: (state) => {
      const updateTime = (event) => {
        const matchStatusId = event.matchStatusId;

        // We cannot increment time for events that are not in play
        if (
          matchStatusId !== EnumMatchStatusName.FIRST_HALF &&
          matchStatusId !== EnumMatchStatusName.SECOND_HALF &&
          matchStatusId !== EnumMatchStatusName.FIRST_EXTRA &&
          matchStatusId !== EnumMatchStatusName.SECOND_EXTRA
        ) {
          return;
        }

        const deltaUpdateTimeInSeconds = (Date.now() - event.LastUpdateTime) / 1000;

        if (deltaUpdateTimeInSeconds < 10 || deltaUpdateTimeInSeconds > 120) {
          return;
        }

        const matchTimeTextLimitsInMinutes = {
          [EnumMatchStatusName.FIRST_HALF]: 45,
          [EnumMatchStatusName.SECOND_HALF]: 90,
          [EnumMatchStatusName.FIRST_EXTRA]: 105,
          [EnumMatchStatusName.SECOND_EXTRA]: 120
        };

        const matchTimeLimitInSeconds = (matchTimeTextLimitsInMinutes[matchStatusId] ?? 150) * 60;

        const hasValidStoppageTime = event.timeTextInternal >= matchTimeLimitInSeconds;

        //Event in extra time
        if (hasValidStoppageTime) {
          event.timeTextStoppageInternal += deltaUpdateTimeInSeconds;
          event.LastUpdateTime = Date.now();
          return;
        }

        event.timeTextInternal += deltaUpdateTimeInSeconds;

        event.LastUpdateTime = Date.now();

        if (event.timeTextInternal > matchTimeLimitInSeconds) {
          const exceedAmount = event.timeTextInternal - matchTimeLimitInSeconds;
          event.timeTextInternal = matchTimeLimitInSeconds;
          event.timeTextStoppageInternal += exceedAmount;

          return;
        }
      };

      state.liveMatches.forEach((liveEventId) => {
        const liveEvent = state.events[liveEventId];
        if (!liveEvent || liveEvent.sport !== sports.Football) {
          return;
        }

        updateTime(liveEvent);
      });
    },
    setMatchFavorite: (state, action) => {
      const { providerId, isFavorite } = action.payload;
      if (state.events[providerId]) {
        state.events[providerId].isFavorite = isFavorite;
      }
    },
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    setSelectedTournament: (state, action) => {
      state.selectedTournament = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFullEvent.fulfilled, (state, action) => {
        addMatchesToState(state.events, [action.payload]);
      })
      .addCase(fetchTopMatches.fulfilled, (state, action) => {
        const topMatches = filterAllowedEvents(action.payload);
        state.topMatches = topMatches
          .sort((a, b) => {
            const dateA = new Date(a.gameDate) ?? 0;
            const dateB = new Date(b.gameDate) ?? 0;
            return dateA - dateB;
          })
          .map((c) => c.providerId);
        addMatchesToState(state.events, topMatches);
        topMatches.forEach((event) => {
          setTournamentName(state.tournamentNames, event);
        });
      })
      .addCase(fetchClearFavorites.fulfilled, (state, action) => {
        const clearFavoritesMatches = filterAllowedEvents(action.payload);
        state.clearFavorites = clearFavoritesMatches.map((c) => c.providerId);
        addMatchesToState(state.events, clearFavoritesMatches);
        clearFavoritesMatches.forEach((event) => {
          setTournamentName(state.tournamentNames, event);
        });
      })
      .addCase(fetchLiveMatches.fulfilled, (state, action) => {
        const liveMatches = filterAllowedEvents(action.payload);
        state.liveMatches = liveMatches.map((c) => c.providerId);
        addMatchesToState(state.events, liveMatches);
        liveMatches.forEach((event) => {
          setTournamentName(state.tournamentNames, event);
        });
      })
      .addCase(fetchSportCountsWithHours.fulfilled, (state, action) => {
        const {
          categoryNames,
          counts
          // topMatches
        } = action.payload;
        state.counts = { ...counts };
        state.categoryNames = { ...categoryNames };
      })
      .addCase(fetchSportCounts.fulfilled, (state, action) => {
        const { counts } = action.payload;
        state.timeCounts = { ...counts };
      })
      .addCase(fetchCompetitorsNameByLanguage.fulfilled, (state, action) => {
        const { data, language } = action.payload;
        state.locales[language.id.split('-')[0]] = {
          ...state.locales[language.id.split('-')[0]],
          ...data
        };
      })
      .addCase(fetchEventsByCategoryWithHours.fulfilled, (state, action) => {
        const { betEvents } = action.payload;
        const objToEventsArr = filterAllowedEvents(Object.values(betEvents).flat());
        addMatchesToState(state.events, objToEventsArr, 0);
        objToEventsArr.forEach((event) => {
          setTournamentName(state.tournamentNames, event);
        });
      })
      .addCase(fetchEventsByCategory.fulfilled, (state, action) => {
        const { betEvents } = action.payload;
        const objToEventsArr = filterAllowedEvents(Object.values(betEvents).flat());
        addMatchesToState(state.events, objToEventsArr, 0);
        objToEventsArr.forEach((event) => {
          setTournamentName(state.tournamentNames, event);
        });
        state.filteredEventIds = objToEventsArr.map((c) => c.providerId);
      })
      .addCase(fetchEventsBySport.fulfilled, (state, action) => {
        const { betEvents } = action.payload;
        const objToEventsArr = filterAllowedEvents(
          Object.values(betEvents)
            .map((c) => Object.values(c))
            .flat()
            .flat()
        );
        addMatchesToState(state.events, objToEventsArr, 0);
        objToEventsArr.forEach((event) => {
          setTournamentName(state.tournamentNames, event);
        });
        state.filteredEventIds = objToEventsArr.map((c) => c.providerId);
      })
      .addCase(getBetSlipMatches.fulfilled, (state, action) => {
        const events = filterAllowedEvents(action.payload);
        if (events && events.length > 0) {
          addMatchesToState(state.events, events, 1);
          events.forEach((event) => {
            setTournamentName(state.tournamentNames, event);
          });
        }
      })
      .addCase(fetchNextLiveMatches.fulfilled, (state, action) => {
        const { betEvents: rawBetEvents, ...meta } = action.payload;
        const betEvents = filterAllowedEvents(rawBetEvents);
        state.nextLiveMatchesMeta = meta;
        state.nextLiveMatches = [
          ...state.nextLiveMatches,
          ...betEvents
            .filter((c) => c && !checkIfLiveEvent(c))
            .sort((a, b) => {
              const dateA = new Date(a.gameDate) ?? 0;
              const dateB = new Date(b.gameDate) ?? 0;
              return dateA - dateB;
            })
            .map((c) => c.providerId)
        ];
        state.nextLiveMatches = [...new Set(state.nextLiveMatches)];
        addMatchesToState(state.events, betEvents);
        betEvents.forEach((event) => {
          setTournamentName(state.tournamentNames, event);
        });
      })
      .addCase(fetchBetBuilderAvailableSelections.pending, (state) => {
        state.betBuilderLoading = true;
        state.betBuilderError = null;
      })
      .addCase(fetchBetBuilderAvailableSelections.fulfilled, (state, action) => {
        const { matchId, data } = action.payload;
        state.betBuilderAvailableSelections[matchId] = data;
        state.betBuilderLoading = false;
        state.betBuilderError = null;
      })
      .addCase(fetchBetBuilderAvailableSelections.rejected, (state, action) => {
        state.betBuilderLoading = false;
        state.betBuilderError = action.error.message;
      })
      .addCase(toggleFavorite.fulfilled, (state, action) => {
        const { gameNumber, success, isFavorite } = action.payload;
        if (success && state.events[gameNumber]) {
          
          if (isFavorite !== undefined) {
            
            const currentIsFavorite = state.events[gameNumber].isFavorite;
            if (isFavorite === true) {
             
              state.events[gameNumber].isFavorite = true;
            } else if (isFavorite === false && currentIsFavorite === true) {
              
              state.events[gameNumber].isFavorite = false;
            }
            
          } else {
            
            state.events[gameNumber].isFavorite = !state.events[gameNumber].isFavorite;
          }
        }
      })
      .addCase(fetchUserFavorites.fulfilled, (state, action) => {
        const userFavorites = filterAllowedEvents(action.payload || []);
        state.userFavorites = userFavorites.map((c) => c.providerId);
        addMatchesToState(state.events, userFavorites);
        userFavorites.forEach((event) => {
          if (event) {
            setTournamentName(state.tournamentNames, event);
            // Ensure isFavorite is set for user favorites
            if (state.events[event.providerId]) {
              state.events[event.providerId].isFavorite = true;
            }
          }
        });
      })
      .addCase(fetchMaradonaHandicap.fulfilled, (state, action) => {
        const maradonaHandicapMatches = filterAllowedEvents(action.payload);
        state.maradonaHandicap = maradonaHandicapMatches.map((c) => c.providerId);
        addMatchesToState(state.events, maradonaHandicapMatches);
        maradonaHandicapMatches.forEach((event) => {
          setTournamentName(state.tournamentNames, event);
        });
      });
  }
});

export const {
  setSelectedMatch,
  setProducerStatus,
  setSelectedLocale,
  setLocales,
  setSelectedSport,
  setSelectedClearFavoritesSport,
  setCategoryNames,
  setSelectedLeague,
  addMatch,
  updateMatch,
  deleteMatch,
  setSelectedLiveDesktopSport,
  setDesktopSearchParams,
  setNextLiveMatches,
  updateLiveFootballEventsTime,
  setSelectedCategory,
  setSelectedTournament,
  setMatchFavorite
} = matchSlice.actions;

export default matchSlice.reducer;
